import { useTranslation } from 'react-i18next'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'

type FontLoadWarningModalProps = {
  show: boolean
  families: string[]
  onClose: () => void
}

/**
 * Tells the person that the fonts in `families` did not load, so the screen
 * may not match the print, and that reloading the page tries the download
 * again. It only informs: its one button closes it, and saving goes on as
 * usual.
 */
export const FontLoadWarningModal = ({
  show,
  families,
  onClose,
}: FontLoadWarningModalProps) => {
  const { t, i18n } = useTranslation()
  // Joined in the language the copy resolved to, not the requested one: a
  // `?lang=` value is taken unchecked, so the requested language can be a tag
  // Intl.ListFormat throws on, or one the app has no copy for, whose sentences
  // fall back to English.
  const familyList = new Intl.ListFormat(i18n.resolvedLanguage, {
    style: 'long',
    type: 'conjunction',
  }).format(families)

  return (
    <FullscreenModal show={[show, onClose]} hideClose={false}>
      <Flex
        height="300px"
        width="600px"
        column
        center
        gap="32px"
        padding="0 32px"
      >
        <Flex gap="12px" column align="center">
          <Flex gap="12px" align="center">
            <Text size="32px">⚠️</Text>
            <Text
              weight="500"
              size="24px"
              align="center"
              data-testid="font-load-warning-modal-title"
            >
              {t('fontLoadWarningModal.title')}
            </Text>
          </Flex>
          <Flex gap="12px" column align="center">
            <Text
              width="500px"
              align="center"
              data-testid="font-load-warning-modal-message"
            >
              {t('fontLoadWarningModal.message', { families: familyList })}
            </Text>
            <Text
              width="500px"
              align="center"
              data-testid="font-load-warning-modal-reload"
            >
              {t('fontLoadWarningModal.reloadTriesAgain')}
            </Text>
            <Text width="500px" align="center" weight="500" size="14px">
              {t('fontLoadWarningModal.stillSaves')}
            </Text>
          </Flex>
        </Flex>
        <Button
          primary
          onClick={onClose}
          aria-label={t('fontLoadWarningModal.closeAriaLabel')}
          data-testid="font-load-warning-modal-close"
        >
          {t('fontLoadWarningModal.closeButton')}
        </Button>
      </Flex>
    </FullscreenModal>
  )
}
